// API utility for making requests
// Dev: empty so Vite proxy forwards /api to localhost:3001. Production: backend domain (env or default).
export const API_BASE_URL = import.meta.env.DEV
  ? ''
  : (import.meta.env.VITE_API_URL || 'https://dara-api.thes.dev');
const TOKEN_KEY = 'auth_token';

function getAuthHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

function handleUnauthorized() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(TOKEN_KEY);
    window.dispatchEvent(new CustomEvent('auth:session-expired'));
  }
}

// Helper function to handle API errors gracefully
const handleApiError = (error: any, endpoint: string) => {
  console.error(`API Error for ${endpoint}:`, error);
  // Return empty data or default values instead of throwing
  if (endpoint.includes('/cars')) return [];
  if (endpoint.includes('/warehouse-cars')) return [];
  if (endpoint.includes('/sales')) return [];
  if (endpoint.includes('/brands')) return [];
  if (endpoint.includes('/models')) return [];
  if (endpoint.includes('/buyers')) return { buyers: [], pagination: { totalCount: 0 } };
  if (endpoint.includes('/expenses')) return { expenses: [], totalAmount: 0, pagination: { totalCount: 0 } };
  if (endpoint.includes('/expense-categories')) return [];
  if (endpoint.includes('/report')) return { gmvUSD: 0, gmvIQD: 0, totalExpensesUSD: 0, totalExpensesIQD: 0, revenueUSD: 0, revenueIQD: 0, salesCount: 0, expensesCount: 0, dateRange: null };
  if (endpoint.includes('/currencies')) return { success: false, data: [], cached: false };
  if (endpoint.includes('/agreements')) return { agreements: [], pagination: { totalCount: 0 } };
  if (endpoint.includes('/exchange-cars')) return { exchangeCars: [], pagination: { currentPage: 1, totalPages: 1, totalCount: 0, limit: 10, hasNextPage: false, hasPrevPage: false } };
  if (endpoint.includes('/cars-of-dara')) return { cars: [] };
  return null;
};

export const api = {
  get: async (endpoint: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: { ...getAuthHeaders() },
      });
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Unauthorized');
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    } catch (error) {
      return handleApiError(error, endpoint);
    }
  },

  post: async (endpoint: string, data: any) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(data),
      });
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Unauthorized');
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    } catch (error) {
      console.error(`API POST Error for ${endpoint}:`, error);
      throw error;
    }
  },

  put: async (endpoint: string, data: any) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(data),
      });
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Unauthorized');
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    } catch (error) {
      console.error(`API PUT Error for ${endpoint}:`, error);
      throw error;
    }
  },

  getBlob: async (endpoint: string): Promise<Blob> => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: { ...getAuthHeaders() },
    });
    if (response.status === 401) {
      handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.blob();
  },

  delete: async (endpoint: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders() },
      });
      if (response.status === 401) {
        handleUnauthorized();
        throw new Error('Unauthorized');
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      if (response.status === 204) return {};
      return response.json();
    } catch (error) {
      console.error(`API DELETE Error for ${endpoint}:`, error);
      throw error;
    }
  },
};
